import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { BackendClient, type BackendTypes } from '@tf/api';

import { useInvalidateApplicationQueries } from '@/core/api/applications';

export const segmentsQueryOptions = (segmentIdentities: BackendTypes.SegmentIdentity[]) => {
	const segmentsKey = segmentIdentities.reduce(
		(acc, s) => {
			acc[s.graphId] = acc[s.graphId] ?? {};
			acc[s.graphId][s.segmentKind] = true;
			return acc;
		},
		{} as Record<BackendTypes.GraphId, Record<BackendTypes.SegmentKind, true>>,
	);

	return queryOptions({
		queryKey: ['segments', segmentsKey],
		queryFn: async (): Promise<BackendTypes.SegmentsReadData> => {
			const res = await BackendClient.getInstance().segmentsRead(segmentIdentities);
			return res.json();
		},
		// Completely disable cache
		staleTime: 0,
		gcTime: 0,
	});
};

export const useSegmentsQuery = (segmentIdentities: BackendTypes.SegmentIdentity[]) => {
	return useQuery(segmentsQueryOptions(segmentIdentities));
};

export const useSegmentsMutation = () => {
	const invalidateApplicationQueries = useInvalidateApplicationQueries();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			data,
		}: {
			data: BackendTypes.SegmentsWritePayload;
		}): Promise<BackendTypes.SegmentsWriteData> => {
			const res = await BackendClient.getInstance().segmentsWrite(data);
			return res.json();
		},
		onSuccess: (_, params) => {
			invalidateApplicationQueries();
			queryClient.invalidateQueries(segmentsQueryOptions(params.data.map((s) => s.segmentIdentity)));
		},
	});
};
